import type {
  AppointmentPlanningPreviewAppointment,
  AppointmentPlanningPreviewWarning,
} from "../../../../../../api/kehrblatt/planning"
import { ceilPositiveMinutesToStep } from "../../../../../../_shared/_logic/time-granularity"
import { formatDateOnly } from "../../../_logic/dates"

type AppointmentTiming = Pick<
  AppointmentPlanningPreviewAppointment,
  "start_at" | "end_at" | "flex_type"
> &
  Partial<Pick<AppointmentPlanningPreviewAppointment, "timeframe_start_at" | "timeframe_end_at">>

const AFTER_DUE_DATE_WARNING_CODE = "AFTER_DUE_DATE"
const BERLIN_TIME_ZONE = "Europe/Berlin"

const berlinDateFormatter = new Intl.DateTimeFormat("de-DE", {
  timeZone: BERLIN_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
})

const berlinDateKey = (value: string): string | null => {
  const date = new Date(value)

  if (Number.isNaN(date.getTime())) return null

  const parts = Object.fromEntries(
    berlinDateFormatter.formatToParts(date).map((part) => [part.type, part.value]),
  )

  return parts.year && parts.month && parts.day ? `${parts.year}-${parts.month}-${parts.day}` : null
}

const normalizedDateOnly = (value: string | null | undefined): string | null => {
  const date = formatDateOnly(value)

  return date || null
}

export const projectAppointmentDuration = <T extends AppointmentTiming>(
  appointment: T | null | undefined,
  durationMinutes: unknown,
): T | null => {
  if (!appointment) return null

  if (appointment.flex_type === "SOFT") return appointment

  const duration = ceilPositiveMinutesToStep(durationMinutes)
  const startTimestamp = Date.parse(appointment.start_at)

  if (duration === null || !Number.isFinite(startTimestamp)) return appointment

  const endDate = new Date(startTimestamp + duration * 60_000)

  if (Number.isNaN(endDate.getTime())) return appointment

  const endAt = endDate.toISOString()

  return {
    ...appointment,
    end_at: endAt,
    ...(appointment.timeframe_end_at ? { timeframe_end_at: endAt } : {}),
  }
}

export const reconcileAppointmentDeadlineWarnings = ({
  warnings,
  appointment,
  dueDate,
}: {
  warnings: readonly AppointmentPlanningPreviewWarning[] | null | undefined
  appointment: Pick<AppointmentPlanningPreviewAppointment, "start_at"> | null | undefined
  dueDate: string | null | undefined
}): AppointmentPlanningPreviewWarning[] => {
  const warningsWithoutDeadline = (warnings ?? []).filter(
    (warning) => warning.code !== AFTER_DUE_DATE_WARNING_CODE,
  )
  const appointmentDate = appointment?.start_at ? berlinDateKey(appointment.start_at) : null
  const dueDateOnly = normalizedDateOnly(dueDate)

  if (!appointmentDate || !dueDateOnly || appointmentDate <= dueDateOnly) {
    return warningsWithoutDeadline
  }

  return [
    ...warningsWithoutDeadline,
    {
      code: AFTER_DUE_DATE_WARNING_CODE,
      severity: "warning",
      message: "Der geplante Termin liegt nach der gesetzlichen Frist.",
    },
  ]
}
